import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
  Box,
  CircularProgress,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import { formatAmount } from '@omega-energia/utilities';

const HTTP_STATUS_MESSAGES = {
  400: 'Dados inválidos. Verifique o CNAE selecionado.',
  401: 'Não autorizado. Faça login novamente.',
  403: 'Sem permissão para esta ação.',
  404: 'Fatura ou recurso não encontrado.',
  422: 'Operação não permitida para esta fatura.',
  500: 'Erro interno. Tente novamente mais tarde.',
};

function getFriendlyErrorMessage(err) {
  if (!err) {
    return 'Erro ao criar nova PO. Tente novamente.';
  }
  const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message;
  if (typeof msg === 'string' && msg.length > 0 && !msg.includes(' at ') && !msg.includes('createError')) {
    return msg;
  }
  const status = err?.response?.status;
  return status && HTTP_STATUS_MESSAGES[status]
    ? HTTP_STATUS_MESSAGES[status]
    : 'Erro ao criar nova PO. Tente novamente.';
}

const ALLOWED_ACTIVITY_TYPE_CODES = ['17.06', '10.09', '10.02'];

function CnaeLabel({ code, isAtual }) {
  return (
    <Typography variant="body2" style={{ fontWeight: 500 }}>
      {code}
      {isAtual && (
        <Typography component="span" variant="caption" style={{ marginLeft: 6, color: 'rgba(0,0,0,0.54)' }}>
          (atual)
        </Typography>
      )}
    </Typography>
  );
}

CnaeLabel.propTypes = {
  code: PropTypes.string.isRequired,
  isAtual: PropTypes.bool.isRequired,
};

function ModalClonePurchaseOrder({ isOpen, onClose, invoiceData, onSuccess }) {
  const cnaeAtual = invoiceData?.partner?.activityType;
  const [selectedCnae, setSelectedCnae] = useState(cnaeAtual || ALLOWED_ACTIVITY_TYPE_CODES[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    if (isLoading) {
      return;
    }
    setError(null);
    setSelectedCnae(cnaeAtual || ALLOWED_ACTIVITY_TYPE_CODES[0]);
    onClose();
  };

  const handleConfirm = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await onSuccess(selectedCnae);
      handleClose();
    } catch (err) {
      setError(getFriendlyErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const cnaeAlterado = selectedCnae !== cnaeAtual;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Recriar PO com novo CNAE</Typography>
      </DialogTitle>

      <DialogContent>
        <Box mb={2}>
          <Typography variant="subtitle2" style={{ color: 'rgba(0, 0, 0, 0.54)', marginBottom: 4 }}>
            Parceiro
          </Typography>
          <Typography variant="body1">
            {invoiceData?.partner?.businessName || invoiceData?.partner?.tradeName || '--'}
          </Typography>
        </Box>

        <Box mb={2}>
          <Typography variant="subtitle2" style={{ color: 'rgba(0, 0, 0, 0.54)', marginBottom: 4 }}>
            Valor da fatura
          </Typography>
          <Typography variant="body1">{invoiceData?.amount ? formatAmount(invoiceData.amount) : '--'}</Typography>
        </Box>

        <Box mb={2}>
          <Typography variant="subtitle2" style={{ color: 'rgba(0, 0, 0, 0.54)', marginBottom: 4 }}>
            PO atual
          </Typography>
          <Typography variant="body1">{invoiceData?.purchaseOrderNumber || '--'}</Typography>
        </Box>

        <Box mb={3}>
          <Typography variant="subtitle2" style={{ color: 'rgba(0, 0, 0, 0.54)', marginBottom: 4 }}>
            CNAE atual do parceiro
          </Typography>
          <Typography variant="body1">{cnaeAtual || 'Não cadastrado'}</Typography>
        </Box>

        <Box mb={2}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Selecione o novo CNAE para a PO
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup value={selectedCnae} onChange={e => setSelectedCnae(e.target.value)}>
              {ALLOWED_ACTIVITY_TYPE_CODES.map(code => (
                <FormControlLabel
                  key={code}
                  value={code}
                  disabled={isLoading}
                  control={<Radio color="primary" />}
                  label={<CnaeLabel code={code} isAtual={code === cnaeAtual} />}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        {!cnaeAlterado && cnaeAtual && (
          <Box mb={2} p={1.5} style={{ backgroundColor: '#fff3e0', borderRadius: 4, border: '1px solid #ffcc80' }}>
            <Typography variant="body2">
              <strong>Atenção:</strong> o CNAE selecionado é o mesmo que o atual. A operação será executada assim mesmo.
            </Typography>
          </Box>
        )}

        {cnaeAlterado && (
          <Box mb={2} p={1.5} style={{ backgroundColor: '#f3f8ff', borderRadius: 4, border: '1px solid #90caf9' }}>
            <Typography variant="body2">
              <strong>Resumo da alteração:</strong> CNAE {cnaeAtual || '—'} → {selectedCnae}
            </Typography>
          </Box>
        )}

        {invoiceData?.oracleInvoiceStatus && (
          <Box mb={2} p={1.5} style={{ backgroundColor: '#fff8e1', borderRadius: 4, border: '1px solid #ffe082' }}>
            <Typography variant="body2">
              <strong>Atenção:</strong> esta fatura já possui uma invoice Oracle vinculada. Confirme que a recriação da
              PO é necessária.
            </Typography>
          </Box>
        )}

        <Box mb={2} p={1.5} style={{ backgroundColor: '#f5f5f5', borderRadius: 4, border: '1px solid #e0e0e0' }}>
          <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.7)' }}>
            <strong>Observação:</strong> caso a PO já esteja vinculada a uma nota fiscal física, é necessário atualizar
            o número da PO gerada no campo observação do PDF.
          </Typography>
        </Box>

        {error && (
          <Box mt={1} p={1.5} style={{ backgroundColor: '#ffebee', borderRadius: 4, border: '1px solid #ef9a9a' }}>
            <Typography variant="body2" style={{ color: '#c62828' }}>
              {error}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading} color="default">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isLoading}
          color="primary"
          variant="contained"
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isLoading ? 'Criando PO...' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ModalClonePurchaseOrder.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  invoiceData: PropTypes.shape({
    id: PropTypes.string,
    purchaseOrderNumber: PropTypes.string,
    amount: PropTypes.number,
    oracleInvoiceStatus: PropTypes.string,
    partner: PropTypes.shape({
      activityType: PropTypes.string,
      businessName: PropTypes.string,
      tradeName: PropTypes.string,
    }),
  }),
  onSuccess: PropTypes.func.isRequired,
};

ModalClonePurchaseOrder.defaultProps = {
  invoiceData: null,
};

export default ModalClonePurchaseOrder;
